import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { userManagementApi } from '../../../services/api';
import { User, Role } from '../../../types';

interface UserFormProps {
  user?: User;
  onClose: () => void;
  onSuccess: () => void;
  roles: Role[];
}

const UserForm: React.FC<UserFormProps> = ({ user, onClose, onSuccess, roles }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    status: (user?.status || 'active') as 'active' | 'inactive',
    roles: user?.roles?.map((r) => r.id) || [] as number[],
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (roleId: number, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      roles: checked
        ? [...prev.roles, roleId]
        : prev.roles.filter((id) => id !== roleId),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!user && !form.password) {
      setError('Password is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload: any = {
        name: form.name,
        email: form.email,
        status: form.status,
        roles: form.roles,
      };
      if (form.password) {
        payload.password = form.password;
      }

      if (user) {
        await userManagementApi.update(user.id, payload);
      } else {
        await userManagementApi.create(payload);
      }
      onSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to save user');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8" onClick={onClose}>
      <div
        className="theme-bg-primary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">
            {user ? 'Edit User' : 'Create User'}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:opacity-80 rounded-full transition-opacity"
          >
            <X className="w-4 h-4 theme-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter user name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter email address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Password {!user && <span className="text-red-500">*</span>}
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              required={!user}
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              placeholder={user ? 'Leave blank to keep current password' : 'Enter password'}
            />
            {user && (
              <p className="mt-0.5 text-sm theme-text-secondary">Leave blank to keep current password</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Status</label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full theme-border rounded px-2 py-1 text-sm theme-bg-primary theme-text-primary focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Roles</label>
            <div className="theme-border border rounded p-2 max-h-40 overflow-y-auto theme-bg-secondary">
              {roles.length === 0 ? (
                <p className="text-sm theme-text-secondary">No roles available</p>
              ) : (
                <div className="space-y-1.5">
                  {roles.map((role) => (
                    <label key={role.id} className="flex items-center gap-1.5 cursor-pointer hover:opacity-80">
                      <input
                        type="checkbox"
                        checked={form.roles.includes(role.id)}
                        onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                        className="rounded theme-border text-blue-600 focus:ring-blue-500 w-3.5 h-3.5"
                      />
                      <span className="text-sm theme-text-primary">{role.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 theme-border border rounded theme-bg-secondary theme-text-primary hover:opacity-80 text-sm"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50 text-sm"
            >
              {submitting ? 'Saving...' : user ? 'Update User' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default UserForm;

